import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import cx from 'classnames';

import { SettingsManager, useSettings, useSportState } from '@atlas/core';
import { staticRoutes } from '@atlas/routing';
import { NavigationMenu } from '@atlas/ui';

const SCROLL_OF_TOP = {
  [staticRoutes.sportsbook]: 405,
  [staticRoutes.live]: 200,
};

const MarketTypeSelector = ({ isTournament }) => {
  const { id, mainMarketTypes } = useSportState();
  const [activeItem, setActiveItem] = useState();
  const { sportMarketTypes } = useSettings();
  const [scrollOfTop, setScrollOfTop] = useState(SCROLL_OF_TOP[staticRoutes.live]);
  const { pathname } = useLocation();

  const defaultMarketTypes = JSON.parse(sportMarketTypes || '{}');

  const className = cx('market-type-selector', {
    tournament: isTournament,
  });

  useEffect(() => {
    const layoutEl = document.querySelector('.layout-wrapper');

    const handleScroll = () => {
      const markets = document.querySelector('.market-type-selector');
      if (!markets) return;

      if (pathname.includes(staticRoutes.sportsbook)) {
        setScrollOfTop(SCROLL_OF_TOP[staticRoutes.sportsbook]);
      }

      layoutEl.scrollTop > scrollOfTop
        ? markets.classList.add('scrolled')
        : markets.classList.remove('scrolled');
    };

    layoutEl.addEventListener('scroll', handleScroll);

    return () => {
      layoutEl.removeEventListener('scroll', handleScroll);
    };
  }, [scrollOfTop, pathname]);

  // Reset active item while changing sport ID
  useEffect(
    () => () => {
      setActiveItem(undefined);
    },
    [id],
  );

  // Set default market type after changing sport ID
  useEffect(() => {
    if (id && !activeItem) {
      setActiveItem(defaultMarketTypes[id]?.[0] || 'main');
    }
  }, [id, activeItem, defaultMarketTypes]);

  // Save active market to settings
  const setActiveMarketType = useCallback(
    value => {
      setActiveItem(value);

      const marketTypeState = { [id]: value === 'main' ? [] : [value] };
      SettingsManager.updateSettings({
        sportMarketTypes: JSON.stringify({
          ...defaultMarketTypes,
          ...marketTypeState,
        }),
      });
    },
    [id, defaultMarketTypes],
  );

  const marketTypes = useMemo(
    () =>
      (mainMarketTypes || [])
        .filter(({ show }) => show)
        .sort((a, b) => a.orderPosition - b.orderPosition)
        .map(({ id, name }) => ({ id: `${id}`, name })),
    [mainMarketTypes],
  );

  const items = useMemo(
    () => [{ id: 'main', name: 'Main markets' }, ...marketTypes],
    [marketTypes],
  );

  return (
    <NavigationMenu
      className={className}
      items={items}
      onClick={setActiveMarketType}
      selectedItem={activeItem}
    />
  );
};

export default MarketTypeSelector;
